import { getCountryCallingCode } from "libphonenumber-js";
import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction, useEffect } from "react";
import { CTAButtons } from "shared-components/Components/Property/InformationRequest/TextForm/type";
import { ConfigStateContext } from "../Configurations/context";
import { phoneRegion } from "@Utils/phones";

export interface FormCallToActions {
  phone: boolean,
  contact: boolean,
  whatsapp: boolean
}

interface ContactModalData {
  id: any;
  owner: any;
  isProject: boolean;
  projectId?: number;
  defaultMessage?: string;
  source?: string;
  propertyLink?: string;
  isExpress?: boolean;
  expressCallback?: (e) => void;
  ctaToShow: FormCallToActions
}

export type FormData = {
  name: string;
  phone: string;
  email: string;
  message?: string;
  fincatyc?: boolean;
  busca_por_ti?: boolean;
};

type FormDataErrors = {
  name: boolean;
  phone: boolean;
  email: boolean;
  fincatyc?: boolean;
  busca_por_ti?: boolean;
};

export type CodePhoneType = {
	code: string,
	value: string
}

interface ContactModalContextType {
  isVisible: boolean;
  modalData: ContactModalData | null;
  ctaButtons: CTAButtons;
  formData: FormData;
  errors: FormDataErrors;
  selectedCountryCode: CodePhoneType;
  setFormData: Dispatch<SetStateAction<FormData>>
  setErrors: Dispatch<SetStateAction<FormDataErrors>>
  openContactModal: (data: ContactModalData) => void;
  closeContactModal: () => void;
  setSelectedCountryCode: (data: CodePhoneType) => void;
}

const ContactModalContext = createContext<ContactModalContextType | undefined>(undefined);

export const ContactModalProvider = ({ children }: { children: ReactNode }) => {
	const { country_code } = useContext(ConfigStateContext);
  const [isVisible, setIsVisible] = useState(false);
  const [modalData, setModalData] = useState<ContactModalData | null>(null);
  const [ctaButtons, setCtaButtons] = useState<CTAButtons>({
    phone: false,
    whatsapp: false,
    contact: false,
  });
  const [formData, setFormData] = useState({
		name: "",
		phone: "",
		email: "",
		message: "",
		fincatyc: false,
		busca_por_ti: false
	});
  const [errors, setErrors] = useState({
		name: false,
		phone: false,
		email: false,
		message: false,
		fincatyc: false,
	});
  const [selectedCountryCode, setSelectedCountryCode] = useState<CodePhoneType>({
		code: "",
		value: "",
	});
  const [ defaultSelectedCountry, setDefaultSelectedCountry ] = useState({
		code: "",
		value: "",
	})

  useEffect(() => {
		const countrySelected = localStorage.getItem("selectedCountryCode");
		const countrySelectedFormatted = JSON.parse(countrySelected);
    if (countrySelectedFormatted && !!countrySelectedFormatted.code?.length) setSelectedCountryCode(countrySelectedFormatted);
    else {
      localStorage.setItem("selectedCountryCode", JSON.stringify(defaultSelectedCountry));
      setSelectedCountryCode(defaultSelectedCountry);
    }
  }, []);

  useEffect(() => {
    if (country_code) {
      const defaultCountryValue = getCountryCallingCode(phoneRegion(country_code))
      const defaultSelectedCountry = {
        value: `+${defaultCountryValue}`,
        code: phoneRegion(country_code),
      }

      localStorage.setItem("selectedCountryCode", JSON.stringify(defaultSelectedCountry));
      setSelectedCountryCode(defaultSelectedCountry);
      setDefaultSelectedCountry(defaultSelectedCountry)
    }
  }, [country_code]);

  const openContactModal = (data: ContactModalData) => {
    setModalData(data);
    setCtaButtons(data.ctaToShow);
    setIsVisible(true);
  };

  const closeContactModal = () => {
    setIsVisible(false);
    setModalData(null);
    setFormData(prev => ({ ...prev, fincatyc: false }));
    setErrors(prev => ({ ...prev, fincatyc: false }));
  };

  return (
    <ContactModalContext.Provider value={{
      isVisible,
      openContactModal,
      closeContactModal,
      modalData,
      ctaButtons,
      formData,
      setFormData,
      errors,
      setErrors,
      selectedCountryCode,
      setSelectedCountryCode
    }}>
      {children}
    </ContactModalContext.Provider>
  );
};

export const useContactModal = (): ContactModalContextType => {
  const context = useContext(ContactModalContext);
  if (!context) throw new Error("useContactModal must be used within a ContactModalProvider");
  return context;
};
